import { createContext, useContext, useRef } from 'react'
import { cn } from '#app/utils/misc.tsx'
import { OyAvatar } from './oy-avatar.tsx'

/**
 * Chips are how you choose — never a <select> (ADR-020 rule 1). Semantics
 * follow ADR-025: inside a `single` group every chip is a `radio` with
 * `aria-checked` and Arrow/Home/End move FOCUS (selection stays an explicit
 * tap, because most chips post a write the moment they are chosen); inside a
 * `multi` group each chip is an `aria-pressed` toggle.
 *
 * Selected = accent-soft fill + 2px accent border (never colour alone);
 * the `green` tone (komt / aanwezig) = done-soft + done border.
 */
type Mode = 'single' | 'multi'

const ChipModeContext = createContext<Mode>('multi')

const ARROW_KEYS = [
	'ArrowLeft',
	'ArrowRight',
	'ArrowUp',
	'ArrowDown',
	'Home',
	'End',
]

export function OyChipGroup({
	mode,
	label,
	labelledBy,
	className,
	children,
}: {
	mode: Mode
	/** the group's accessible name; prefer `labelledBy` with a visible OyLabel */
	label?: string
	labelledBy?: string
	className?: string
	children: React.ReactNode
}) {
	const ref = useRef<HTMLDivElement>(null)
	function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
		if (mode !== 'single' || !ARROW_KEYS.includes(e.key)) return
		const chips = Array.from(
			ref.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]') ?? [],
		).filter((c) => !c.disabled)
		const i = chips.indexOf(document.activeElement as HTMLButtonElement)
		if (i === -1 || chips.length === 0) return
		e.preventDefault()
		const forward = e.key === 'ArrowRight' || e.key === 'ArrowDown'
		const next =
			e.key === 'Home'
				? 0
				: e.key === 'End'
					? chips.length - 1
					: (i + (forward ? 1 : -1) + chips.length) % chips.length
		chips[next]?.focus()
	}
	return (
		<ChipModeContext.Provider value={mode}>
			<div
				ref={ref}
				role={mode === 'single' ? 'radiogroup' : 'group'}
				aria-label={labelledBy ? undefined : label}
				aria-labelledby={labelledBy}
				className={cn('oy-chips', className)}
				onKeyDown={onKeyDown}
			>
				{children}
			</div>
		</ChipModeContext.Provider>
	)
}

export function OyChip({
	selected,
	onSelect,
	avatar,
	tone = 'accent',
	disabled,
	className,
	children,
	'aria-label': ariaLabel,
}: {
	selected: boolean
	onSelect: () => void
	/** the avatar person chip: initial in the person's hue, on the left */
	avatar?: { initial: string; hue: string }
	tone?: 'accent' | 'green'
	disabled?: boolean
	className?: string
	children: React.ReactNode
	'aria-label'?: string
}) {
	const mode = useContext(ChipModeContext)
	const single = mode === 'single'
	return (
		<button
			type="button"
			role={single ? 'radio' : undefined}
			aria-checked={single ? selected : undefined}
			aria-pressed={single ? undefined : selected}
			aria-label={ariaLabel}
			disabled={disabled}
			onClick={onSelect}
			className={cn(
				'oy-chip',
				selected && 'sel',
				tone === 'green' && 'green',
				avatar && 'has-av',
				className,
			)}
		>
			{avatar ? (
				<OyAvatar name={avatar.initial} hue={avatar.hue} size={28} />
			) : null}
			{children}
		</button>
	)
}
